'use client';

import data from './data.json';

import { ChangeEvent, useEffect, useState } from 'react';

import { SearchField } from '@/components/Forms/Fields/SearchField';
import { InsightsIntro } from '@/components/Layouts/Intros/InsightsIntro';
import { Tabs } from '@/components/Insights/Tabs';
import { allInsightsUniqueTags, DEFAULT_SEARCH_PARAMS, ITEMS_PER_PAGE, TABS_SELECTOR_ID } from '@/constants/insights';
import { Tags } from '@/components/Insights/Tags';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { ArrowDownIcon } from '@/images/svg/logos/ArrowDownIcon';
import { useFilter } from '@/hooks/useFilter';
import { PaginationList } from '@/components/PaginationList';
import { InsightsCard } from '@/components/Cards/InsightsCard';
import { useRemount } from '@/hooks/useRemount';
import { ICardPreview } from '@/interfaces/layout';
import { PREV_BLOG_QUERY } from '@/constants/localStorage';
import { Typography } from '@/atoms/00-ui/Typography';
import { TInsightIds } from '@/interfaces/insights';
import { getInsightsById } from '@/helpers/insights';
import { TopInsightsSection } from '@/organisms/01-sections/TopInsightsSection';

const topInsightsIds = data.topInsights.ids as TInsightIds[];
const topInsights = getInsightsById(topInsightsIds);

export default function InsightsLanding() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const [searchQuery, setSearchQuery] = useState('');
  const [isOpened, setOpen] = useState(false);
  const { key, remount } = useRemount();

  const filteredList = useFilter<ICardPreview>({ list: data.insights.list, searchQuery });
  const urlSearchParams = new URLSearchParams(searchParams);

  const handleChangeQuery = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const clearAllFilters = () => {
    urlSearchParams.delete(DEFAULT_SEARCH_PARAMS.tags);
    urlSearchParams.delete(DEFAULT_SEARCH_PARAMS.category);
    urlSearchParams.set(DEFAULT_SEARCH_PARAMS.page, '1');
    setSearchQuery('');

    router.replace(`${pathname}?${urlSearchParams.toString()}`, {
      scroll: false,
    });
  };

  useEffect(() => {
    remount();
  }, [searchQuery, searchParams, remount]);

  useEffect(() => {
    if (searchParams.has(DEFAULT_SEARCH_PARAMS.tags)) {
      setOpen(true);
    }
  }, []);

  return (
    <>
      <div
        className="
          flex flex-col justify-center
          gap-[32px] mobile:gap-[48px]
          relative z-50 pt-[160px] mobile:pt-[189px] pb-[64px] mobile:pb-[30px]
        "
      >
        <InsightsIntro
          preTitle={data.intro.preTitle}
          postTitle={data.intro.postTitle}
          title={data.intro.title}
          supportingTextClass="text-black-8 mobile:text-black-6"
        >
          <div className="w-full flex flex-col gap-y-base mobile:gap-y-[40px]">
            <Typography
              component="h2"
              variant="preTitle"
              className="text-purple-8"
            >
              {data.topInsights.title}
            </Typography>

            <TopInsightsSection insights={topInsights} />
          </div>
        </InsightsIntro>

        <div className="flex flex-col gap-2xl">
          <div className="flex mobile:flex-row flex-col items-start gap-base mobile:items-center">
            <SearchField
              inputClassname="pl-[42px] pr-[14px]"
              wrapperClassname="h-[48px] mobile:w-[320px] w-full"
              query={searchQuery}
              placeholder="Search insights"
              handleChange={handleChangeQuery}
            />

            <div
              onClick={() => setOpen((prevState) => !prevState)}
              className="flex shrink-0 self-end mobile:self-center gap-[8px] items-center text-black-10
                group active:text-purple-8 hover:text-purple-7 cursor-pointer duration-300"
            >
              <span>Filter by tags</span>
              <ArrowDownIcon
                className={`${isOpened ? 'rotate-180' : 'rotate-0'} duration-300`}
                strokeClassName="group-hover:stroke-purple-7 group-active:stroke-purple-8 transition-[stroke]
                  duration-300"
              />
            </div>
            <div
              className="flex items-center justify-center cursor-pointer select-none text-black-10 duration-300
                hover:scale-[1.05] hover:text-purple-9
                group active:text-purple-8 px-[12px] py-[9px] pr-[8px]"
              onClick={clearAllFilters}
            >
              Clear all
            </div>
          </div>

          <div
            className={`w-full mobile:pb-[66px]
              grid duration-300 transition-[grid-template-rows] 
              ${isOpened ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}
            `}
          >
            <Tags
              tags={allInsightsUniqueTags}
              tagsWrapperClass="gap-xs mobile:gap-base"
            />
          </div>
        </div>
      </div>

      <section
        className="
          relative z-50 w-full min-h-[300px] flex flex-col gap-[32px] md-tablet:gap-[64px] items-center
        "
      >
        <Tabs
          tabs={data.insights.tabs}
          variant="default"
          setPageSearchParams
        />

        <div key={key}>
          <PaginationList<ICardPreview>
            listClassName="
              w-full grid grid-cols-1 gap-[48px] mobile:gap-[20px] max-w-[590px]
              md-tablet:max-w-full md-1:grid-cols-2 xl-1:grid-cols-3 grid-rows-[1fr]
            "
            list={filteredList.sort((current, next) => {
              return Date.parse(next?.publishedOn as string) - Date.parse(current?.publishedOn as string);
            })}
            storageParam={PREV_BLOG_QUERY}
            listItemComponent={InsightsCard}
            tabsSelectorId={TABS_SELECTOR_ID}
            listItemsPerPage={ITEMS_PER_PAGE}
            activeTags={
              searchParams.get(DEFAULT_SEARCH_PARAMS.tags)
                ? searchParams.get(DEFAULT_SEARCH_PARAMS.tags)!.split(',')
                : null
            }
            activeLinkBgCLassName="bg-purple-2"
            swiperArrow
          />
        </div>
      </section>
    </>
  );
}
